import React, { useState, useEffect } from 'react';
import { 
  Target, 
  Eye, 
  Award, 
  Users, 
  CheckCircle, 
  ChevronRight,
  Globe,
  TrendingUp,
  Heart,
  Shield,
  Star,
  Zap,
  Rocket
} from 'lucide-react';
import { Link } from 'react-router-dom';
import httpClient from '../../Api/axios';

function AboutUs() {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get('/getall__aboutus');
      
      // Handle different response formats
      let aboutItems = [];
      
      if (Array.isArray(response.data)) {
        aboutItems = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          aboutItems = response.data.data;
        } else if (Array.isArray(response.data.items)) {
          aboutItems = response.data.items;
        } else if (response.data._id) {
          aboutItems = [response.data];
        } else {
          aboutItems = Object.values(response.data).filter(item => 
            item && typeof item === 'object' && item.About_us_title
          );
        }
      }
      
      if (!Array.isArray(aboutItems)) {
        aboutItems = [];
      }
      
      // Filter to show only active items
      const activeAboutItems = aboutItems.filter(item => 
        item?.status === "Active" || item?.status === "active"
      );
      
      // Take the first active item (or null if none)
      setAboutData(activeAboutItems.length > 0 ? activeAboutItems[0] : null);
      setError(null);
    } catch (err) {
      setError(`Failed to fetch about us data: ${err.message || 'Please try again later.'}`);
      console.error('Error fetching about us:', err);
      setAboutData(null);
    } finally {
      setLoading(false);
    }
  };

  // Updated team stats with new icons and values
  const teamStats = [
    { icon: Users, value: '50+', label: 'Team Members' },
    { icon: Globe, value: '100+', label: 'Projects Completed' },
    { icon: TrendingUp, value: '95%', label: 'Success Rate' },
    { icon: Award, value: '25+', label: 'Awards Won' }
  ];

  // Updated values with red/blue theme
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Transparent and honest communication with accountability at every level.',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Pushing boundaries with cutting-edge technologies and creative solutions.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Genuine commitment to excellence and client success.',
      color: 'from-red-600 to-orange-500'
    },
    {
      icon: Star,
      title: 'Excellence',
      description: 'Striving for the highest quality in every project we undertake.',
      color: 'from-blue-600 to-purple-500'
    }
  ];

  const milestones = [
    { year: '2018', title: 'Company Founded', description: 'Started with passionate technology experts' },
    { year: '2019', title: 'First Major Client', description: 'Landed our first enterprise client partnership' },
    { year: '2020', title: 'Team Expansion', description: 'Grew to 50+ dedicated team members' },
    { year: '2021', title: 'Award Recognition', description: 'Won industry recognition for excellence' },
    { year: '2022', title: 'Global Expansion', description: 'Established multiple delivery centers' },
    { year: '2023', title: 'Milestone Projects', description: 'Completed 100+ successful projects' }
  ];

  // Loading skeleton with new theme
  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-100 to-red-50 border border-red-200 flex items-center justify-center">
                <Rocket className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-red-600 via-gray-800 to-blue-600 bg-clip-text text-transparent">
                About Us
              </span>
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-red-600 to-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg">Loading about us information...</p>
          </div>
          {/* Loading skeleton for hero section */}
          <div className="grid lg:grid-cols-2 gap-12 items-center animate-pulse">
            <div>
              <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
              <div className="h-16 bg-gray-200 rounded w-3/4 mb-6"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6 mb-8"></div>
              <div className="flex flex-wrap gap-4 mb-10">
                <div className="h-12 bg-gray-200 rounded w-40"></div>
                <div className="h-12 bg-gray-200 rounded w-40"></div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gray-200 h-[400px] w-full rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-100 to-red-50 border border-red-200 flex items-center justify-center">
            <Award className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={fetchAboutData}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If no active about data found
  if (!aboutData) {
    return (
      <div className="min-h-screen bg-white text-gray-900 py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-100 to-red-50 border border-red-200 flex items-center justify-center">
                <Rocket className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-red-600 via-gray-800 to-blue-600 bg-clip-text text-transparent">
                About Us
              </span>
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-red-600 to-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg">No active about us content available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Top Banner Section */}
<section
  className="relative h-[60vh] md:h-[70vh] flex items-center justify-center bg-cover bg-center"
  style={{ backgroundImage: "url('/aboutus.png')" }} 
>
  

 
</section>

      {/* Hero Section with Dynamic Data */}
      <section className="relative overflow-hidden py-12 md:py-24 px-4 md:px-8">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Column - Dynamic Content */}
            <div>
             
              {/* ABOUT US TITLE */}
  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
    {aboutData.About_us_title || "About KANDAX"}
  </h2>
              
              <p className="text-gray-600 text-base md:text-lg lg:text-xl mb-6 md:mb-8 leading-relaxed">
                {aboutData.About_us_description || 'At KANDAX, we are more than just developers - we are innovators, problem-solvers, and digital architects. With a passion for technology and a commitment to excellence, we help businesses navigate the digital landscape and achieve their goals.'}
              </p>
              
              
            </div>
            
            {/* Right Column - Dynamic Image */}
           {/* Right Column - Dynamic Image */}
<div className="relative mt-8 lg:mt-0 flex justify-center">
  <div className="relative rounded-xl md:rounded-2xl overflow-hidden border border-gray-200 shadow-2xl max-w-lg w-full">
    <img
      src={aboutData.About_us_image || "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"}
      alt={aboutData.About_us_title || "KANDAX Team"}
      className="w-full h-auto object-contain"
      onError={(e) => {
        e.target.src =
          "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
      }}
    />
  </div>
</div>

          </div>
        </div>
      </section>

     

      {/* Mission & Vision Section with ID for scrolling */}
      <section id="mission-section" className="py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
           
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">
              <span className="bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                Our Mission & Vision
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Driving innovation and delivering excellence in every project we undertake
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Mission Card */}
            <div className="bg-white border-2 border-gray-100 rounded-xl md:rounded-2xl p-6 md:p-8 hover:border-red-500/30 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 md:w-7 md:h-7 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-600 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">
                To empower businesses with innovative digital solutions that drive growth, 
                enhance efficiency, and create meaningful impact. We strive to be the trusted 
                technology partner that transforms challenges into opportunities.
              </p>
              <ul className="space-y-2 md:space-y-3">
                {['Digital Transformation', 'Innovation Driven', 'Client Success', 'Quality Assurance'].map((item, index) => (
                  <li key={index} className="flex items-center gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Vision Card */}
            <div className="bg-white border-2 border-gray-100 rounded-xl md:rounded-2xl p-6 md:p-8 hover:border-blue-500/30 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 md:w-7 md:h-7 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-600 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">
                To be the global leader in digital innovation, setting new standards for 
                technological excellence and creating a future where technology serves as a 
                catalyst for positive change and sustainable growth.
              </p>
              <ul className="space-y-2 md:space-y-3">
                {['Global Leadership', 'Technological Excellence', 'Sustainable Growth', 'Positive Impact'].map((item, index) => (
                  <li key={index} className="flex items-center gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
           
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">
              <span className="bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                Our Core Values
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 hover:border-red-500/50 hover:transform hover:-translate-y-1 transition-all duration-300 group shadow-sm hover:shadow-lg"
                >
                  <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br ${value.color} flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-xs md:text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
 {/* Stats Section */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {teamStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div 
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg md:rounded-xl p-4 md:p-6 text-center hover:border-red-500/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full bg-gradient-to-br from-red-50 to-blue-50 border border-gray-100 flex items-center justify-center">
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-red-600" />
                  </div>
                  <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-red-600 mb-1 md:mb-2">{stat.value}</div>
                  <div className="text-gray-600 text-xs md:text-sm lg:text-base">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* Timeline Section */}
      <section className="py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">
              <span className="bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                Our Journey
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Milestones in our journey of innovation and growth
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-1 h-full bg-gradient-to-b from-red-600 via-blue-600 to-red-600"></div>
            
            {milestones.map((milestone, index) => (
              <div 
                key={index}
                className={`relative flex flex-col md:flex-row items-center mb-8 md:mb-12 ${
                  index % 2 === 0 ? 'md:flex-row-reverse' : ''
                }`}
              >
                {/* Timeline Node */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 md:w-8 md:h-8 rounded-full bg-gradient-to-r from-red-600 to-blue-600 border-4 border-white z-10 shadow-lg"></div>
                
                {/* Content */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-8 md:pr-12 md:text-right' : 'md:pl-8 md:pl-12'}`}>
                  <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 ml-10 md:ml-0 mt-8 md:mt-0 shadow-sm hover:shadow-md transition-shadow">
                    <div className="text-red-600 font-bold text-xl md:text-2xl mb-1 md:mb-2">{milestone.year}</div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{milestone.title}</h3>
                    <p className="text-gray-600 text-sm md:text-base">{milestone.description}</p>
                  </div>
                </div>
                
                {/* Spacer for mobile */}
                <div className="md:w-2/12"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      
    </div>
  );
}

export default AboutUs;